/**
 * The online fight protocol (docs/architecture.md §7): small binary messages over one channel,
 * the same bytes whatever the transport (WebSocket now, WebTransport later). Every message starts
 * with its type byte. Numbers are little-endian. Axis values travel as int8 (the simulation
 * quantizes to the same steps), buttons as a bit mask.
 *
 *   client → server
 *     INPUT  [u8 1][u32 firstTick][u8 n] n × [i8 mx][i8 my][u8 buttons]
 *     PING   [u8 2][f64 clientTime]
 *   server → client
 *     FRAMES [u8 10][u32 firstTick][u16 n] n × (fighter 0, fighter 1) × [i8 mx][i8 my][u8 buttons]
 *     HASH   [u8 11][u32 tick][u32 hash][i8 slack0][i8 slack1]
 *     PONG   [u8 12][f64 clientTime][f64 serverTick]
 *
 * Everything that is not per tick (match found, start, end, resync) is JSON over the transport's
 * own message channel.
 */
import { NO_INPUT, type FighterInput } from '../sim/input';

/**
 * The version of the client–server contract: these messages, the JSON ones and the join options.
 * Raise it with any change an older client can't follow, and raise MIN_CLIENT_PROTOCOL with it:
 * the server then turns older clients away, and they fetch the new build (client/src/platform/
 * update.ts). Clients from before this field count as 1. 2 (2026-10-02): six new fighters (D42), which
 * older builds can't draw. 3 (2026-10-02): health back between rounds (D45), which older builds
 * wouldn't simulate. 4 (2026-10-03): stronger Power and Takedown, the new Speed and skill damage in
 * the fight state (D48), which older builds would simulate differently. 5 (2026-10-04): the new
 * Escape and Footwork (D49), likewise. 6 (2026-10-04): the new Guard, Reach and Focus (D51).
 */
export const PROTOCOL_VERSION = 6;
/** The oldest client the server still lets in. */
export const MIN_CLIENT_PROTOCOL = 6;

export const MSG = {
  input: 1,
  ping: 2,
  frames: 10,
  hash: 11,
  pong: 12,
} as const;

export type Frame = readonly [FighterInput, FighterInput];

const INPUT_BYTES = 3;
const MAX_INPUTS = 255;
const MAX_FRAMES = 65535;

function axisByte(v: number): number {
  return Math.round(Math.max(-1, Math.min(1, v)) * 127);
}

/** The input exactly as the other side will decode it (what the server simulates with). */
export function wireInput(i: FighterInput): FighterInput {
  return { mx: axisByte(i.mx) / 127, my: axisByte(i.my) / 127, buttons: i.buttons & 15 };
}

function writeInput(v: DataView, at: number, i: FighterInput): void {
  v.setInt8(at, axisByte(i.mx));
  v.setInt8(at + 1, axisByte(i.my));
  v.setUint8(at + 2, i.buttons & 15);
}

function readInput(v: DataView, at: number): FighterInput {
  const mx = v.getInt8(at) / 127;
  const my = v.getInt8(at + 1) / 127;
  const buttons = v.getUint8(at + 2) & 15;
  return mx === 0 && my === 0 && buttons === 0 ? NO_INPUT : { mx, my, buttons };
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** The message type, or −1 for an empty buffer. */
export function messageType(bytes: Uint8Array): number {
  return bytes.byteLength > 0 ? bytes[0]! : -1;
}

// ---------------------------------------------------------------------------------------------
// Client → server

export function encodeInputs(firstTick: number, inputs: readonly FighterInput[]): Uint8Array {
  const n = Math.min(inputs.length, MAX_INPUTS);
  const bytes = new Uint8Array(6 + n * INPUT_BYTES);
  const v = view(bytes);
  v.setUint8(0, MSG.input);
  v.setUint32(1, firstTick, true);
  v.setUint8(5, n);
  for (let k = 0; k < n; k++) writeInput(v, 6 + k * INPUT_BYTES, inputs[k]!);
  return bytes;
}

/** Null when the message is malformed (wrong type or size): the server drops it. */
export function decodeInputs(bytes: Uint8Array): { firstTick: number; inputs: FighterInput[] } | null {
  if (bytes.byteLength < 6 || bytes[0] !== MSG.input) return null;
  const v = view(bytes);
  const firstTick = v.getUint32(1, true);
  const n = v.getUint8(5);
  if (bytes.byteLength !== 6 + n * INPUT_BYTES) return null;
  const inputs: FighterInput[] = [];
  for (let k = 0; k < n; k++) inputs.push(readInput(v, 6 + k * INPUT_BYTES));
  return { firstTick, inputs };
}

export function encodePing(clientTime: number): Uint8Array {
  const bytes = new Uint8Array(9);
  const v = view(bytes);
  v.setUint8(0, MSG.ping);
  v.setFloat64(1, clientTime, true);
  return bytes;
}

export function decodePing(bytes: Uint8Array): number | null {
  if (bytes.byteLength !== 9 || bytes[0] !== MSG.ping) return null;
  return view(bytes).getFloat64(1, true);
}

// ---------------------------------------------------------------------------------------------
// Server → client

export function encodeFrames(firstTick: number, frames: readonly Frame[]): Uint8Array {
  const n = Math.min(frames.length, MAX_FRAMES);
  const bytes = new Uint8Array(7 + n * 2 * INPUT_BYTES);
  const v = view(bytes);
  v.setUint8(0, MSG.frames);
  v.setUint32(1, firstTick, true);
  v.setUint16(5, n, true);
  for (let k = 0; k < n; k++) {
    const at = 7 + k * 2 * INPUT_BYTES;
    writeInput(v, at, frames[k]![0]);
    writeInput(v, at + INPUT_BYTES, frames[k]![1]);
  }
  return bytes;
}

export function decodeFrames(bytes: Uint8Array): { firstTick: number; frames: Frame[] } | null {
  if (bytes.byteLength < 7 || bytes[0] !== MSG.frames) return null;
  const v = view(bytes);
  const firstTick = v.getUint32(1, true);
  const n = v.getUint16(5, true);
  if (bytes.byteLength !== 7 + n * 2 * INPUT_BYTES) return null;
  const frames: Frame[] = [];
  for (let k = 0; k < n; k++) {
    const at = 7 + k * 2 * INPUT_BYTES;
    frames.push([readInput(v, at), readInput(v, at + INPUT_BYTES)]);
  }
  return { firstTick, frames };
}

/** `slack`: how many ticks early each fighter's inputs have been arriving lately (−128…127). */
export function encodeHash(tick: number, hash: number, slack: readonly [number, number]): Uint8Array {
  const bytes = new Uint8Array(11);
  const v = view(bytes);
  v.setUint8(0, MSG.hash);
  v.setUint32(1, tick, true);
  v.setUint32(5, hash >>> 0, true);
  v.setInt8(9, Math.max(-128, Math.min(127, Math.round(slack[0]))));
  v.setInt8(10, Math.max(-128, Math.min(127, Math.round(slack[1]))));
  return bytes;
}

export function decodeHash(bytes: Uint8Array): { tick: number; hash: number; slack: [number, number] } | null {
  if (bytes.byteLength !== 11 || bytes[0] !== MSG.hash) return null;
  const v = view(bytes);
  return { tick: v.getUint32(1, true), hash: v.getUint32(5, true), slack: [v.getInt8(9), v.getInt8(10)] };
}

/** `serverTick`: the server's fight clock in (fractional) ticks when it answered. */
export function encodePong(clientTime: number, serverTick: number): Uint8Array {
  const bytes = new Uint8Array(17);
  const v = view(bytes);
  v.setUint8(0, MSG.pong);
  v.setFloat64(1, clientTime, true);
  v.setFloat64(9, serverTick, true);
  return bytes;
}

export function decodePong(bytes: Uint8Array): { clientTime: number; serverTick: number } | null {
  if (bytes.byteLength !== 17 || bytes[0] !== MSG.pong) return null;
  const v = view(bytes);
  return { clientTime: v.getFloat64(1, true), serverTick: v.getFloat64(9, true) };
}

// ---------------------------------------------------------------------------------------------
// Full state (resync, reconnect): JSON that keeps −0, which plain JSON turns into 0.

const NEG_ZERO = '-0';

export function serializeState(value: unknown): string {
  return JSON.stringify(value, (_k, v: unknown) => (typeof v === 'number' && Object.is(v, -0) ? NEG_ZERO : v));
}

export function deserializeState<T>(json: string): T {
  return JSON.parse(json, (_k, v: unknown) => (v === NEG_ZERO ? -0 : v)) as T;
}
